/**
 * One poll of `GET /api/v1/me/waiting` for the whole page (ADO 2546): the bell,
 * the Notifications page and Home read the same list, so their items and
 * counts always agree, and the server is asked once a minute instead of once
 * per reader.
 *
 * - **Polling while someone reads it.** The first reader to mount starts it,
 *   the last to unmount stops it. The shell remounts on every page change, so
 *   a reader that mounts while the answer is under a minute old does not fetch
 *   again — it gets the answer it has.
 * - **Background requests** (`X-Daquma-Background`, ADR-0046): an open tab
 *   does not keep the session from its idle sign-out.
 * - **Never across people.** Keyed on the signed-in account; another account
 *   or a sign-out drops the answer. Tab memory only.
 * - The token getter is read through a ref (`useAccessToken()` returns a new
 *   function on most renders; an effect that listed it would loop).
 */

import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { useMsal } from "@azure/msal-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { getWaiting, type WaitingItem } from "@/shared/api/waiting";

const POLL_MS = 60_000;

export interface WaitingState {
  /** null until the first answer arrives. */
  items: WaitingItem[] | null;
  /** The last request failed (the last good list, if any, is kept). */
  failed: boolean;
}

interface Store extends WaitingState {
  key: string;
  fetchedAt: number;
}

const EMPTY: Store = { key: "", items: null, failed: false, fetchedAt: 0 };

let store: Store = EMPTY;
let readers = 0;
let timer: number | undefined;
let inFlight: Promise<void> | null = null;
let tokenOf: (() => Promise<string>) | null = null;
const listeners = new Set<() => void>();

function set(next: Partial<Store>): void {
  store = { ...store, ...next };
  for (const listener of listeners) listener();
}

/** Same items, in the same order? An item's id changes when what is waiting
 *  changes, so ids are enough — and keeping the old array avoids re-renders. */
function same(a: WaitingItem[] | null, b: WaitingItem[]): boolean {
  return a !== null && a.length === b.length && a.every((item, i) => item.id === b[i]?.id);
}

function load(): Promise<void> {
  if (inFlight) return inFlight;
  const key = store.key;
  const getToken = tokenOf;
  if (!key || !getToken) return Promise.resolve();
  inFlight = (async () => {
    try {
      const out = await getWaiting(await getToken(), { background: true });
      if (store.key === key) {
        set({
          items: same(store.items, out.items) ? store.items : out.items,
          failed: false,
          fetchedAt: Date.now(),
        });
      }
    } catch {
      if (store.key === key) set({ failed: true, fetchedAt: Date.now() });
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

function startPolling(): void {
  if (timer !== undefined) return;
  timer = window.setInterval(() => void load(), POLL_MS);
}

function stopPolling(): void {
  if (timer === undefined) return;
  window.clearInterval(timer);
  timer = undefined;
}

/** Ask again now (the Notifications page's Refresh). */
export function refreshWaiting(): Promise<void> {
  return load();
}

/** Forget everything — for tests, which sign different people in. */
export function resetWaitingStore(): void {
  stopPolling();
  store = EMPTY;
  readers = 0;
  inFlight = null;
  tokenOf = null;
  listeners.clear();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot(): Store {
  return store;
}

export function useWaitingItems(): WaitingState & { refresh: () => void } {
  const { accounts } = useMsal();
  const accountKey = accounts.map((a) => a.homeAccountId).join("|");
  const getToken = useAccessToken();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const current = useSyncExternalStore(subscribe, snapshot, snapshot);

  useEffect(() => {
    if (!accountKey) return undefined;
    tokenOf = () => getTokenRef.current();
    if (store.key !== accountKey) {
      // Another person (or the first reader): never show the previous answer.
      inFlight = null;
      set({ ...EMPTY, key: accountKey });
    }
    readers += 1;
    startPolling();
    if (Date.now() - store.fetchedAt >= POLL_MS) void load();
    return () => {
      readers -= 1;
      if (readers <= 0) {
        readers = 0;
        stopPolling();
      }
    };
  }, [accountKey]);

  const refresh = useCallback(() => void load(), []);
  const mine = current.key === accountKey;
  return {
    items: mine ? current.items : null,
    failed: mine ? current.failed : false,
    refresh,
  };
}
